/**
 * Variant Selection — pure logic (no React, no UI, no side effects).
 *
 * Sprint 3 (storefront presentation + selection ONLY).
 * Must mirror server semantics: order-independent, case-insensitive matching.
 * See /app/backend/models/product_variant.py (combination_key).
 *
 * NOTE: This module is intentionally decoupled from admin variantHelpers to
 * keep storefront concerns isolated. It only consumes the SANITIZED public
 * variant fields exposed by GET /products/{slug}.
 */

export function normalizeOptionValue(s) {
  return String(s ?? "").trim().toLowerCase();
}

/** Build a combination key from an option-values array (order-insensitive). */
export function buildSelectionKey(optionValues) {
  return (optionValues || [])
    .map((ov) => `${normalizeOptionValue(ov?.group)}=${normalizeOptionValue(ov?.value)}`)
    .sort()
    .join("|");
}

/** Build key from a partial selection map { groupName: value }. Only keys
 * whose value is a non-empty string are included (partial-safe). */
export function selectionMapToKey(selectionMap) {
  const ovs = Object.entries(selectionMap || {})
    .filter(([, v]) => typeof v === "string" && v.trim() !== "")
    .map(([g, v]) => ({ group: g, value: v }));
  return buildSelectionKey(ovs);
}

/** Return true if variant is active + non-deleted (defensive — public API
 * should already filter, but the storefront may cache older payloads). */
export function isVariantAvailable(v) {
  return !!(v && v.is_active !== false);
}

/** Find the exact variant that matches a complete selection map. Returns
 * null if no match or if selection is incomplete relative to option_groups. */
export function findMatchingVariant(variants, optionGroups, selectionMap) {
  const groups = optionGroups || [];
  if (!groups.length) return null;
  // Selection must contain every group
  for (const g of groups) {
    const v = selectionMap?.[g.name];
    if (typeof v !== "string" || !v.trim()) return null;
  }
  const key = selectionMapToKey(selectionMap);
  for (const v of variants || []) {
    if (!isVariantAvailable(v)) continue;
    if (buildSelectionKey(v.option_values) === key) return v;
  }
  return null;
}

/** Return all variants compatible with the given partial selection.
 * A variant is compatible when all selected (group,value) pairs match. */
export function getCompatibleVariants(variants, selectionMap) {
  const sel = Object.entries(selectionMap || {})
    .filter(([, v]) => typeof v === "string" && v.trim() !== "");
  if (!sel.length) return (variants || []).filter(isVariantAvailable);
  const norm = sel.map(([g, v]) => [normalizeOptionValue(g), normalizeOptionValue(v)]);
  return (variants || []).filter((variant) => {
    if (!isVariantAvailable(variant)) return false;
    const ovMap = new Map();
    for (const ov of variant.option_values || []) {
      ovMap.set(normalizeOptionValue(ov?.group), normalizeOptionValue(ov?.value));
    }
    return norm.every(([g, v]) => ovMap.get(g) === v);
  });
}

/**
 * Return true if `candidate` (value in `groupName`) leads to at least one
 * valid ACTIVE variant given the customer's other selections. The tested
 * pair is temporarily merged into the selection, then compatibility is
 * evaluated. Availability does NOT consider stock (out-of-stock variants
 * are still "selectable" for UX transparency); use `isSelectionInStock`
 * for stock decisions.
 */
export function isOptionValueAvailable(variants, selectionMap, groupName, candidate) {
  const merged = { ...(selectionMap || {}), [groupName]: candidate };
  const compat = getCompatibleVariants(variants, merged);
  return compat.length > 0;
}

/** Effective price = sale_price when valid + strictly lower than price. */
export function getEffectiveVariantPrice(variant) {
  if (!variant) return null;
  const p = Number(variant.price);
  if (!Number.isFinite(p)) return null;
  const sp = variant.sale_price;
  if (sp !== null && sp !== undefined && Number.isFinite(Number(sp)) && Number(sp) >= 0 && Number(sp) < p) {
    return Number(sp);
  }
  return p;
}

/** Return { min, max } across ACTIVE variants using effective price. Or null
 * when no active variant. */
export function getVariantPriceRange(variants) {
  const actives = (variants || []).filter(isVariantAvailable);
  if (!actives.length) return null;
  let min = Infinity, max = -Infinity;
  for (const v of actives) {
    const p = getEffectiveVariantPrice(v);
    if (p === null) continue;
    if (p < min) min = p;
    if (p > max) max = p;
  }
  if (!Number.isFinite(min)) return null;
  return { min, max };
}

/** Resolve which image index to show for a variant. Returns the numeric
 * index if valid within `images`, otherwise `null` (caller falls back to
 * the current active image). Simple products / no image_index → null. */
export function resolveVariantImage(variant, images) {
  const idx = variant?.image_index;
  if (idx === null || idx === undefined) return null;
  const n = Number(idx);
  if (!Number.isInteger(n) || n < 0) return null;
  if (!Array.isArray(images) || n >= images.length) return null;
  return n;
}

/**
 * Determine if the product should present variant UX at all (Sprint 3
 * fail-closed rules): must have `has_variants=true` AND public API must
 * expose a non-empty `variants` array (feature-gate on backend already
 * filtered inactive/deleted). Missing has_variants OR empty variants →
 * behave as simple product.
 */
export function isVariantProduct(product) {
  if (!product) return false;
  if (!product.has_variants) return false;
  const vs = product.variants;
  if (!Array.isArray(vs) || vs.length === 0) return false;
  const groups = product.option_groups;
  if (!Array.isArray(groups) || groups.length === 0) return false;
  return true;
}

/** Clamp qty to [1, stock] safely. */
export function clampQuantity(qty, stock) {
  const s = Math.max(0, parseInt(stock, 10) || 0);
  const q = parseInt(qty, 10) || 1;
  if (s <= 0) return 1;
  return Math.min(Math.max(1, q), s);
}
